import React, {
  createContext,
  useContext,
  useState,
  useMemo,
} from 'react';

import { useToast } from '@gluestack-ui/themed';

import { AppToast, type AppToastCta } from '../components/AppToast';
import { PopupDialog } from '../components/PopupDialog';

type ToastPlacement = 'top' | 'top right' | 'top left' | 'bottom' | 'bottom left' | 'bottom right';
type ToastVariant = 'outline' | 'solid' | 'accent';
type ToastAction = 'error' | 'success' | 'warning' | 'info' | 'attention';

interface DialogButton {
  text: string;
  variant?: string;
  action?: string;
  onPress?: (closeDialog?: () => void) => void;
  testID?: string;
  preventAutoClose?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
}

interface DialogProps {
  title: string;
  message: string;
  buttons?: DialogButton[];
  size?: string;
  children?: React.ReactNode;
  isDisabled?: boolean;
  isPersistent?: boolean;
}

// Split into actions and state internally
interface DialogActions {
  showDialog: (props: DialogProps) => void;
  updateDialogProps: (partial: Partial<DialogProps>) => void;
  hideDialog: () => void;
  showToastMessage: (
    action: ToastAction,
    message: string,
    title?: string,
    placement?: ToastPlacement,
    duration?: number,
    variant?: ToastVariant,
    cta?: AppToastCta,
  ) => void;
}

interface DialogState {
  dialogProps: DialogProps;
  dialogIsVisible: boolean;
}

// But keep the public interface the same
interface DialogContextType extends DialogActions, DialogState {}

const defaultDialogProps: DialogProps = {
  title: '',
  message: '',
  buttons: [],
};

// Create separate internal contexts
const DialogActionsContext = createContext<DialogActions | null>(null);
const DialogStateContext = createContext<DialogState>({
  dialogProps: defaultDialogProps,
  dialogIsVisible: false,
});

// But expose only the combined context type externally
export const DialogContext = createContext<DialogContextType>({} as DialogContextType);

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialogProps, setDialogProps] = useState<DialogProps>(defaultDialogProps);
  const [dialogIsVisible, setDialogIsVisible] = useState(false);

  const toast = useToast();

  // Memoize all actions
  const actions = useMemo(() => {
    const showDialog = (props: DialogProps) => {
      setDialogProps(props);
      setDialogIsVisible(true);
    };

    const updateDialogProps = (partial: Partial<DialogProps>) => {
      setDialogProps((prev) => ({ ...prev, ...partial }));
    };

    const hideDialog = () => {
      setDialogIsVisible(false);
    };

    const showToastMessage = (
      action: ToastAction,
      message: string,
      title = '',
      placement: ToastPlacement = 'top',
      duration = 3000,
      variant: ToastVariant = 'accent',
      cta?: AppToastCta,
    ) => {
      toast.show({
        placement,
        duration,
        render: ({ id }) => (
          <AppToast
            id={id}
            action={action}
            variant={variant}
            title={title}
            message={message}
            mt="$12"
            ctaLabel={cta?.label}
            ctaTestID={cta?.testID}
            onCtaPress={cta
              ? () => {
                toast.close(id);
                cta.onPress();
              }
              : undefined}
          />
        ),
      });
    };

    return {
      showDialog,
      updateDialogProps,
      hideDialog,
      showToastMessage,
    };
  }, [toast]);

  // Memoize state
  const state = useMemo(
    () => ({
      dialogProps,
      dialogIsVisible,
    }),
    [dialogProps, dialogIsVisible],
  );

  const isPersistent = Boolean(dialogProps.isPersistent);

  return (
    <DialogActionsContext.Provider value={actions}>
      <DialogStateContext.Provider value={state}>
        {children}
        {dialogIsVisible ? (
          <PopupDialog
            size={dialogProps.size}
            isOpen={dialogIsVisible}
            title={dialogProps.title}
            message={dialogProps.message}
            buttons={dialogProps.buttons}
            showCloseButton={!isPersistent}
            onClose={isPersistent ? () => {} : actions.hideDialog}
          >
            {dialogProps.children}
          </PopupDialog>
        ) : null}
      </DialogStateContext.Provider>
    </DialogActionsContext.Provider>
  );
};

// Internal hooks for optimization
const useDialogActions = () => {
  const context = useContext(DialogActionsContext);
  if (!context) throw new Error('useDialogActions must be used within DialogProvider');
  return context;
};

const useDialogState = () => useContext(DialogStateContext);

// Keep the public API the same
export const useDialog = () => {
  const actions = useDialogActions();
  const state = useDialogState();

  // Combine them, but memoize to prevent unnecessary object creation
  return useMemo(
    () => ({
      ...actions,
      ...state,
    }),
    [actions, state],
  );
};
